import React, { useEffect, useMemo, useRef, useState } from 'react';

// Type-to-search picker shared by the desktop and mobile "write a PO" forms.
// It used to live twice — once in AdminPurchaseOrders, once in CreatePO — and the
// two copies drifted. One component now, so a fix lands on both.
//
// Free text is allowed: whatever you type is committed as the value even if it
// isn't in the list, which is how vendors that aren't in the vendor table still
// get onto a PO.

const MAX_SHOWN = 50;

function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

// Ranks a match so the name you're typing toward comes first: exact, then
// starts-with, then a word inside the name starting with it, then anywhere.
// Straight alphabetical substring order buried the obvious answer — typing
// "tra" put "Traffic Sign & Signal Cover" below four vendors that merely
// contain "tra", and past the old 8-row cut-off entirely.
function rank(label, q) {
  const l = (label || '').toLowerCase();
  if (l === q) return 0;
  if (l.startsWith(q)) return 1;
  if (new RegExp(`\\b${escapeRe(q)}`).test(l)) return 2;
  return l.includes(q) ? 3 : -1;
}

// freeText: vendor boxes keep whatever you type; the project box can't, because a
// name that matches nothing just leaves the job unset. The "no match" line says so.
export default function ComboBox({ value, onChange, options, placeholder, style, size = 'desktop', inputClassName, freeText = true, noun = 'match' }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);   // keyboard-highlighted row, -1 = none
  const ref = useRef();
  const listRef = useRef();
  // True while the user is typing. Without it you could not search this box at all
  // once something was picked: typing calls onChange('', text) to clear the committed
  // selection, the parent's `value` therefore changes to '', and the sync below fired
  // and wiped the very text being typed. It only worked on an empty box, where value
  // was '' before and after — which is why a NEW PO searched fine and editing one
  // did not.
  const typing = useRef(false);

  useEffect(() => { if (!typing.current) setQuery(value || ''); }, [value]);

  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) { typing.current = false; setOpen(false); } }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Until you type, show the whole list rather than filtering by what's already
  // committed — otherwise focusing a field that says "Wedco" showed you Wedco and
  // nothing else, and you had to clear it by hand before you could browse.
  const q = typing.current ? query.trim().toLowerCase() : '';
  const { shown, total } = useMemo(() => {
    const all = options || [];
    if (!q) return { shown: all.slice(0, MAX_SHOWN), total: all.length };
    const hits = [];
    for (const o of all) { const r = rank(o.label, q); if (r >= 0) hits.push({ o, r }); }
    hits.sort((a, b) => a.r - b.r || a.o.label.localeCompare(b.o.label));
    return { shown: hits.slice(0, MAX_SHOWN).map(h => h.o), total: hits.length };
  }, [options, q]);

  // Keep the highlighted row in range and scrolled into view.
  useEffect(() => { setCursor(shown.length ? 0 : -1); }, [q, shown.length]);
  useEffect(() => {
    const el = listRef.current && listRef.current.children[cursor];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  function select(opt) {
    typing.current = false;
    setQuery(opt.label);
    onChange(opt.value, opt.label);
    setOpen(false);
  }

  function handleInput(e) {
    typing.current = true;
    setQuery(e.target.value);
    onChange('', e.target.value);
    setOpen(true);
  }

  // Arrow keys / Enter / Escape, so a name can be picked without reaching for
  // the mouse — the whole point of typing it in the first place.
  function handleKey(e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const d = e.key === 'ArrowDown' ? 1 : -1;
      setCursor(c => { const n = c + d; return n < 0 ? shown.length - 1 : n >= shown.length ? 0 : n; });
    } else if (e.key === 'Enter') {
      // Swallow Enter whenever the menu is open, so a stray press picks a row or
      // does nothing — never falls through and submits the PO form underneath.
      if (open) {
        e.preventDefault();
        if (cursor >= 0 && shown[cursor]) select(shown[cursor]);
        else setOpen(false);
      }
    } else if (e.key === 'Escape') {
      if (open) { e.stopPropagation(); setOpen(false); }
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  }

  const mobile = size === 'mobile';
  const inputStyle = mobile
    ? { width: '100%', padding: '13px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, boxSizing: 'border-box', background: '#fff' }
    : undefined;
  const rowStyle = active => ({
    padding: mobile ? '12px 14px' : '9px 12px',
    fontSize: mobile ? 15 : 13,
    cursor: 'pointer',
    borderBottom: '1px solid #f3f4f6',
    background: active ? '#eff6ff' : '#fff',
  });
  const noteStyle = { padding: mobile ? '10px 14px' : '8px 12px', fontSize: mobile ? 13 : 12, color: '#9ca3af' };

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <input
        className={inputClassName !== undefined ? inputClassName : (mobile ? undefined : 'field-input')}
        value={query}
        onChange={handleInput}
        onKeyDown={handleKey}
        onFocus={e => { setOpen(true); e.target.select(); }}
        placeholder={placeholder}
        autoComplete="off"
        style={inputStyle}
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', border: mobile ? '1.5px solid #ddd' : '1.5px solid var(--border)', borderRadius: mobile ? 10 : 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2, maxHeight: 260, overflowY: 'auto' }}>
          <div ref={listRef}>
            {shown.map((o, i) => (
              <div
                key={o.value}
                onMouseDown={() => select(o)}
                onMouseEnter={() => setCursor(i)}
                style={rowStyle(i === cursor)}
              >
                {o.label}
              </div>
            ))}
          </div>
          {/* Say so when the list is cut short, instead of silently hiding the
              rest — the old 8-row cap dropped 117 of 125 matches with no hint. */}
          {total > shown.length && (
            <div style={noteStyle}>Showing {shown.length} of {total} — keep typing to narrow it down</div>
          )}
          {/* And say so when nothing matches, instead of the menu just vanishing
              and looking broken. */}
          {shown.length === 0 && (
            <div style={{ ...noteStyle, color: freeText ? '#6b7280' : '#b45309' }}>
              No {noun} for “{query.trim()}”
              {freeText ? ' — it will be saved exactly as you typed it.' : ' — pick one from the list.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
